import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Combobox, ComboboxList, ComboboxOption } from '../../components/combobox.js';
import { matchesName } from '../../lib/name-match.js';
import type { ProjectMember } from '../../lib/plans-api.js';

// #91: Aktionsleiste der Mehrfachauswahl. Sticky am unteren Tabellenrand (Zielschirm 1280×660:
// die Leiste muss ohne Scrollen erreichbar sein, während man oben markiert). Tester-Wahl über
// die #82-Combobox (Teiltreffer über Name), nicht über ein neues Muster.
export function BulkActionBar({
  count,
  members,
  pending,
  readOnly = false,
  onAssign,
  onRemoveTester,
  onSetScope,
  onClear,
}: {
  count: number;
  members: ProjectMember[];
  pending: boolean;
  /**
   * #114 (A9): Der Plan ist abgeschlossen — jede Sammelaktion liefe in 409 `plan_closed`
   * (assert-plan-open.ts). Die Leiste bleibt sichtbar, ihre schreibenden Knöpfe sind gesperrt;
   * die Begründung steht EINMAL oben an der Tabelle (scope-table.tsx), nicht an jedem Knopf.
   * „Auswahl aufheben" bleibt bedienbar — das schreibt nichts.
   */
  readOnly?: boolean;
  onAssign: (testerId: string) => void;
  onRemoveTester: () => void;
  onSetScope: (inScope: boolean) => void;
  onClear: () => void;
}) {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  if (count === 0) return null;
  const hits = members.filter((m) => matchesName(query, m.name)).slice(0, 30);
  return (
    <div className="sticky bottom-0 z-10 flex flex-wrap items-center gap-3 border-t border-[var(--border)] bg-bg-surface px-4 py-2 text-xs shadow-md">
      <span className="font-semibold text-text-primary">{t('plans.bulk.selected', { count })}</span>
      <div className="w-52">
        <Combobox
          label={t('plans.bulk.assign')}
          value={query}
          onChange={setQuery}
          placeholder={t('plans.bulk.assign')}
          disabled={readOnly}
          className="w-full rounded-btn border border-[var(--border)] bg-bg-surface px-2 py-1 disabled:opacity-60"
        >
          {({ open, close, listboxId }) =>
            open && (
              <ComboboxList id={listboxId}>
                {hits.map((m) => (
                  <ComboboxOption
                    key={m.user_id}
                    onSelect={() => {
                      onAssign(m.user_id);
                      setQuery('');
                      close();
                    }}
                  >
                    {m.name}
                  </ComboboxOption>
                ))}
              </ComboboxList>
            )
          }
        </Combobox>
      </div>
      <button
        type="button"
        disabled={pending || readOnly}
        onClick={onRemoveTester}
        className="rounded-btn border border-[var(--border)] px-2 py-1 disabled:opacity-60"
      >
        {t('plans.bulk.removeTester')}
      </button>
      <button
        type="button"
        disabled={pending || readOnly}
        onClick={() => onSetScope(true)}
        className="rounded-btn border border-[var(--border)] px-2 py-1 disabled:opacity-60"
      >
        {t('plans.bulk.intoScope')}
      </button>
      <button
        type="button"
        disabled={pending || readOnly}
        onClick={() => onSetScope(false)}
        className="rounded-btn border border-[var(--border)] px-2 py-1 disabled:opacity-60"
      >
        {t('plans.bulk.outOfScope')}
      </button>
      <button
        type="button"
        onClick={onClear}
        className="ml-auto text-text-muted hover:text-text-body"
      >
        {t('plans.bulk.clear')}
      </button>
    </div>
  );
}
